/**
 * Session handling for short-lived access tokens.
 *
 * The backend now issues a 60-minute access token plus a rotating refresh
 * token. This module keeps the stored access token fresh by refreshing it a
 * little before it expires, so every existing `fetch(createRequest(...))` call
 * (which reads localStorage['token'] at call time) keeps working without
 * change. It also exposes storeSession() for login and logout() for sign-out.
 */
import { apiUrl } from '../api_service/api_url';

const ACCESS_KEY = 'token';
const REFRESH_KEY = 'refreshToken';

// Modules that keep per-session state outside localStorage (e.g. the Redux MFA
// gate) register here so sign-out resets them without this module importing
// the store.
const logoutHooks: Array<() => void> = [];
export function onLogout(fn: () => void): void {
  logoutHooks.push(fn);
}

const SKEW_MS = 2 * 60 * 1000; // refresh this long before expiry

type SessionResponse = { accessToken?: string; refreshToken?: string; expiresIn?: number };

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<boolean> | null = null;

function parseJwt(token: string): any | null {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function accessExpiryMs(): number | null {
  const token = localStorage.getItem(ACCESS_KEY);
  if (!token) return null;
  const claims = parseJwt(token);
  return claims?.exp ? claims.exp * 1000 : null;
}

/** True when there is a usable session: a live access token or a refresh token. */
export function hasSession(): boolean {
  if (localStorage.getItem(REFRESH_KEY)) return true;
  const exp = accessExpiryMs();
  return exp != null && Date.now() < exp;
}

export function storeSession(res: SessionResponse): void {
  if (res.accessToken) localStorage.setItem(ACCESS_KEY, res.accessToken);
  if (res.refreshToken) localStorage.setItem(REFRESH_KEY, res.refreshToken);
  scheduleRefresh();
}

/** Refresh now (single-flight). Returns true on success. */
export function refreshNow(): Promise<boolean> {
  if (inFlight) return inFlight;
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return Promise.resolve(false);

  inFlight = fetch(`${apiUrl}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })
    .then(async (r) => {
      if (!r.ok) return false;
      const data: SessionResponse = await r.json();
      if (!data.accessToken) return false;
      storeSession(data);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

function scheduleRefresh(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  const exp = accessExpiryMs();
  if (exp == null || !localStorage.getItem(REFRESH_KEY)) return;
  const delay = Math.max(0, exp - Date.now() - SKEW_MS);
  refreshTimer = setTimeout(() => {
    void refreshNow();
  }, delay);
}

/** Call once on app start: if a refresh token exists, keep the access token
 *  fresh (refreshing immediately when it is already close to or past expiry). */
export function initSession(): void {
  if (!localStorage.getItem(REFRESH_KEY)) return;
  const exp = accessExpiryMs();
  if (exp == null || Date.now() > exp - SKEW_MS) {
    void refreshNow();
  } else {
    scheduleRefresh();
  }
}

/** Sign out: tell the backend to revoke the refresh token, then clear storage. */
export async function logout(): Promise<void> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  if (refreshToken) {
    try {
      await fetch(`${apiUrl}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      /* best effort */
    }
  }
  localStorage.clear();
  // A pending MFA hand-off (token + deep link) must never outlive the user.
  ['mfaChallengeToken', 'mfaEnrollToken', 'mfaNext'].forEach((k) => sessionStorage.removeItem(k));
  logoutHooks.forEach((fn) => {
    try { fn(); } catch { /* best effort */ }
  });
}
